var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
var __generator = (this && this.__generator) || function (thisArg, body) {
    var _ = { label: 0, sent: function() { if (t[0] & 1) throw t[1]; return t[1]; }, trys: [], ops: [] }, f, y, t, g;
    return g = { next: verb(0), "throw": verb(1), "return": verb(2) }, typeof Symbol === "function" && (g[Symbol.iterator] = function() { return this; }), g;
    function verb(n) { return function (v) { return step([n, v]); }; }
    function step(op) {
        if (f) throw new TypeError("Generator is already executing.");
        while (g && (g = 0, op[0] && (_ = 0)), _) try {
            if (f = 1, y && (t = op[0] & 2 ? y["return"] : op[0] ? y["throw"] || ((t = y["return"]) && t.call(y), 0) : y.next) && !(t = t.call(y, op[1])).done) return t;
            if (y = 0, t) op = [op[0] & 2, t.value];
            switch (op[0]) {
                case 0: case 1: t = op; break;
                case 4: _.label++; return { value: op[1], done: false };
                case 5: _.label++; y = op[1]; op = [0]; continue;
                case 7: op = _.ops.pop(); _.trys.pop(); continue;
                default:
                    if (!(t = _.trys, t = t.length > 0 && t[t.length - 1]) && (op[0] === 6 || op[0] === 2)) { _ = 0; continue; }
                    if (op[0] === 3 && (!t || (op[1] > t[0] && op[1] < t[3]))) { _.label = op[1]; break; }
                    if (op[0] === 6 && _.label < t[1]) { _.label = t[1]; t = op; break; }
                    if (t && _.label < t[2]) { _.label = t[2]; _.ops.push(op); break; }
                    if (t[2]) _.ops.pop();
                    _.trys.pop(); continue;
            }
            op = body.call(thisArg, _);
        } catch (e) { op = [6, e]; y = 0; } finally { f = t = 0; }
        if (op[0] & 5) throw op[1]; return { value: op[0] ? op[1] : void 0, done: true };
    }
};
var __spreadArray = (this && this.__spreadArray) || function (to, from, pack) {
    if (pack || arguments.length === 2) for (var i = 0, l = from.length, ar; i < l; i++) {
        if (ar || !(i in from)) {
            if (!ar) ar = Array.prototype.slice.call(from, 0, i);
            ar[i] = from[i];
        }
    }
    return to.concat(ar || Array.prototype.slice.call(from));
};
/**
 * Full definition of a GPT Language Model, all of it in this single file.
 *
 * This is a tensorflow.js version of pytorch-based minGPT from Andrej Karpathy:
 * - https://github.com/karpathy/ng-video-lecture
 * - https://github.com/karpathy/nanoGPT
 * - https://github.com/karpathy/minGPT
 *
 * To understand what's going on here please check this detailed and nicely explained lecture from Andrej Karpathy:
 * - https://www.youtube.com/watch?v=kCc8FmEb1nY
 *
 * This is a faster (but harder to grasp) version of the model from the ./model-easier.ts file.
 * - It processes all `Heads` inside `CausalSelfAttention` in parallel (instead of sequentially).
 */
import * as tf from '@tensorflow/tfjs';
import { countParams, dispose, withLayerHelpers, withModelHelpers } from './utils';
// GPT Language Model
export function GPT(params) {
    var _this = this;
    var nLayer = params.nLayer, nHead = params.nHead, nEmbd = params.nEmbd, vocabSize = params.vocabSize, blockSize = params.blockSize, _a = params.embdDropout, embdDropout = _a === void 0 ? 0.1 : _a, _b = params.residDropout, residDropout = _b === void 0 ? 0.1 : _b, _c = params.attnDropout, attnDropout = _c === void 0 ? 0.1 : _c;
    var modelIsWarm = false; // Whether model weights are initialized yet or not
    var transformer = {
        wte: tf.layers.embedding({ name: 'wte', inputDim: vocabSize + 1, outputDim: nEmbd, embeddingsInitializer: embeddingsInitializer, maskZero: true }),
        wpe: tf.layers.embedding({ name: 'wpe', inputDim: blockSize, outputDim: nEmbd, embeddingsInitializer: embeddingsInitializer, inputShape: [blockSize] }),
        drop: tf.layers.dropout({ name: 'drop', rate: embdDropout }),
        add: tf.layers.add({ name: 'add' }),
        h: Array.from({ length: nLayer }, function (_, i) { return Block({ nEmbd: nEmbd, nHead: nHead, blockSize: blockSize, attnDropout: attnDropout, residDropout: residDropout, nLayer: nLayer, name: "block".concat(i + 1) }); }),
        lnF: tf.layers.layerNormalization({ name: 'lnF' }), // Final normalization layer
    };
    var lmHead = tf.layers.dense({ name: 'lmHead', units: vocabSize, useBias: false, kernelInitializer: kernelInitializer });
    var model = {
        params: params,
        apply: function (idx) { return tf.tidy(function () {
            var _a = idx.shape, B = _a[0], T = _a[1]; // B - batch size, T - time dimension (block size)
            if (T !== blockSize)
                throw new Error("Sequence must be of size ".concat(blockSize, ", got ").concat(T));
            var tokEmb = transformer.wte.apply(idx);
            var pos = tf.range(0, T, 1).reshape([1, T]); // Expand dims to match shape [1, T]
            var posBatched = pos.tile([B, 1]); // Repeat position indices for every item in a batch
            var posEmb = transformer.wpe.apply(posBatched);
            var x = transformer.add.apply([tokEmb, posEmb]);
            x = transformer.drop.apply(x);
            transformer.h.forEach(function (block) {
                x = block.apply(x);
            });
            x = transformer.lnF.apply(x);
            // Unnormalized outputs of a model's last linear layer before applying an activation function like Softmax
            var logits = lmHead.apply(x);
            return logits;
        }); },
        loss: function (idx, targets) { return tf.tidy(function () {
            var logits = model.apply(idx);
            var _a = logits.shape, B = _a[0], T = _a[1], C = _a[2]; // B - batch dimension, T - time dimension, C - embeddings dimension
            var flattenLogits = logits.reshape([B * T, C]);
            var flattenTargets = targets.reshape([B * T]);
            var targetsOneHot = tf.oneHot(flattenTargets, vocabSize);
            var loss = tf.losses.softmaxCrossEntropy(targetsOneHot, flattenLogits);
            return loss;
        }); },
        // Take a sequence of indices idx (tensor of shape (B, T), with 0 as a mask) and complete the
        // sequence maxNewTokens times, feeding the predictions back into the model each time
        generate: function (params, onGenerateChar) { return __awaiter(_this, void 0, void 0, function () {
            var maxNewTokens, _a, temperature, _b, doSample, topK, idx, _loop_1, i;
            return __generator(this, function (_c) {
                switch (_c.label) {
                    case 0:
                        maxNewTokens = params.maxNewTokens, _a = params.temperature, temperature = _a === void 0 ? 1.0 : _a, _b = params.doSample, doSample = _b === void 0 ? false : _b, topK = params.topK;
                        idx = params.idx;
                        _loop_1 = function (i) {
                            var idxNext, idxPrev, nextToken;
                            return __generator(this, function (_d) {
                                switch (_d.label) {
                                    case 0:
                                        idxNext = tf.tidy(function () {
                                            var T = idx.shape[1];
                                            var idxShaped = tf.concat([
                                                // If idx is too long - truncate the beginning of time dimension and keep the end
                                                idx.slice([0, Math.max(0, T - blockSize)], [-1, Math.min(T, blockSize)]),
                                                // If idx is too short - pad the time dimension with -1 (keep the beginning)
                                                tf.zeros([idx.shape[0], Math.max(0, blockSize - T)], 'int32'),
                                            ], 1);
                                            // Forward the model to get the logits for the index in the sequence
                                            var logits = model.apply(idxShaped); // (B,T,C)
                                            // Focus only on the last time step (all from first axis, last from second axis, all from third axis)
                                            // Remove the second axis dimension (because it is 1 after the slice) using tf.squeeze()
                                            var lastCharLogits = logits.slice([0, i < blockSize ? i : blockSize - 1, 0], [-1, 1, -1]).squeeze([1]); // Becomes (B, C)
                                            // Scale by desired temperature
                                            lastCharLogits = tf.div(lastCharLogits, tf.scalar(temperature));
                                            if (topK) {
                                                var values = lastCharLogits.topk(Math.min(topK, vocabSize)).values;
                                                var smallestTopK = values.slice([0, values.shape[1] - 1]); // Last element in the array, since topk sorts the values
                                                lastCharLogits = lastCharLogits.where(lastCharLogits.greaterEqual(smallestTopK), tf.scalar(-Infinity));
                                            }
                                            // Apply softmax to convert logits to (normalized) probabilities
                                            var probs = tf.softmax(lastCharLogits); // (B, C)
                                            var idxNext;
                                            // Either sample from the distribution or take the most likely element
                                            if (doSample) {
                                                var backend = tf.getBackend();
                                                if (backend === 'webgpu') {
                                                    // 1st sample from tf.multinomial is always zero in webgpu backend
                                                    // @see: https://github.com/tensorflow/tfjs/issues/8057
                                                    idxNext = tf.multinomial(probs, 2, undefined, true).slice([0, 1], [1, 1]); // (B, 1)
                                                }
                                                else if (backend === 'tensorflow') {
                                                    // TF Node backend does not support normalized logits passed to multinomial
                                                    idxNext = tf.multinomial(lastCharLogits, 1); // (B, 1)
                                                }
                                                else {
                                                    idxNext = tf.multinomial(probs, 1, undefined, true); // (B, 1)
                                                }
                                            }
                                            else {
                                                idxNext = probs.argMax(-1).expandDims(-1);
                                            }
                                            return idxNext;
                                        });
                                        idxPrev = idx;
                                        idx = idx.concat(idxNext, 1); // (B, T+1)
                                        if (!onGenerateChar) return [3 /*break*/, 2];
                                        return [4 /*yield*/, idxNext.array()];
                                    case 1:
                                        nextToken = (_d.sent())[0][0];
                                        onGenerateChar(nextToken);
                                        _d.label = 2;
                                    case 2:
                                        dispose([idxNext, idxPrev]);
                                        // For browsers: unblock the main thread (allow the UI to be re-rendered)
                                        return [4 /*yield*/, tf.nextFrame()];
                                    case 3:
                                        // For browsers: unblock the main thread (allow the UI to be re-rendered)
                                        _d.sent();
                                        return [2 /*return*/];
                                }
                            });
                        };
                        i = 0;
                        _c.label = 1;
                    case 1:
                        if (!(i < maxNewTokens)) return [3 /*break*/, 4];
                        return [5 /*yield**/, _loop_1(i)];
                    case 2:
                        _c.sent();
                        _c.label = 3;
                    case 3:
                        i++;
                        return [3 /*break*/, 1];
                    case 4: return [2 /*return*/, idx];
                }
            });
        }); },
        optimizer: function (params) {
            return tf.train.adam(params.learningRate);
        },
        build: function () { return tf.tidy(function () {
            if (modelIsWarm)
                return;
            // Perform a test prediction to build the layers and initialize default weights.
            model.apply(tf.zeros([1, blockSize]));
            modelIsWarm = true;
        }); },
        summary: function () { return tf.tidy(function () {
            model.build();
            // Report number of parameters (note we don't count the decoder parameters in lmHead)
            var wte = transformer.wte, wpe = transformer.wpe, add = transformer.add, drop = transformer.drop, lnF = transformer.lnF, h = transformer.h;
            var params = countParams(__spreadArray([wte, wpe, add, drop, lnF], h, true));
            return { params: params };
        }); },
    };
    return withModelHelpers(model, [transformer.wte, transformer.wpe, transformer.add, transformer.drop, transformer.lnF, transformer.h, lmHead]);
}
// Transformer block: communication followed by computation
function Block(args) {
    var nEmbd = args.nEmbd, nHead = args.nHead, blockSize = args.blockSize, residDropout = args.residDropout, attnDropout = args.attnDropout, nLayer = args.nLayer, name = args.name;
    var ln1 = tf.layers.layerNormalization({ name: "".concat(name, "-ln1") });
    var attn = CausalSelfAttention({ name: "".concat(name, "-attn"), nEmbd: nEmbd, blockSize: blockSize, nHead: nHead, residDropout: residDropout, attnDropout: attnDropout, nLayer: nLayer }); // Self-attention head
    var ln2 = tf.layers.layerNormalization({ name: "".concat(name, "-ln2") });
    var mlp = FeedForward({ name: "".concat(name, "-mlp"), nEmbd: nEmbd, residDropout: residDropout, nLayer: nLayer });
    var block = {
        apply: function (x) {
            x = x.add(attn.apply(ln1.apply(x)));
            x = x.add(mlp.apply(ln2.apply(x)));
            return x;
        },
    };
    return withLayerHelpers(block, [ln1, attn, ln2, mlp]);
}
// Multiple heads of self-attention in parallel
function CausalSelfAttention(args) {
    var nHead = args.nHead, blockSize = args.blockSize, nEmbd = args.nEmbd, attnDropout = args.attnDropout, residDropout = args.residDropout, nLayer = args.nLayer, name = args.name;
    if (nEmbd % nHead !== 0)
        throw new Error("Cannot calculate head size: nEmbd % nHead !== 0");
    var headSize = nEmbd / nHead;
    // The key, query, value projections for all heads, but in a batch (combined into one dense layer for efficiency)
    var cAttn = tf.layers.dense({ name: "".concat(name, "-cAttn"), inputDim: nEmbd, units: nEmbd * 3, useBias: false, kernelInitializer: kernelInitializer });
    // Output projection
    var cProj = tf.layers.dense({ name: "".concat(name, "-cProj"), inputDim: nEmbd, units: nEmbd, kernelInitializer: projectionKernelInitializer(nLayer), biasInitializer: biasInitializer });
    // Regularization
    var attnDrop = tf.layers.dropout({ name: "".concat(name, "-attnDrop"), rate: attnDropout });
    var residDrop = tf.layers.dropout({ name: "".concat(name, "-residDrop"), rate: residDropout });
    // Causal mask to ensure that attention is only applied to the left in the input sequence
    // Create a lower triangular matrix (the equivalent of torch.tril)
    var bias = tf.linalg.bandPart(tf.ones([blockSize, blockSize]), -1, 0).reshape([1, 1, blockSize, blockSize]);
    var multiHeadAttention = {
        apply: function (x) { return tf.tidy(function () {
            var _a = x.shape, B = _a[0], T = _a[1], C = _a[2];
            // Calculate query, key, values for all heads in batch and move head forward to be the batch dim
            var qkv = cAttn.apply(x);
            var q = qkv.slice([0, 0, 0], [-1, -1, C]).reshape([B, T, nHead, C / nHead]).transpose([0, 2, 1, 3]); // (B, nh, T, hs)
            var k = qkv.slice([0, 0, C], [-1, -1, C]).reshape([B, T, nHead, C / nHead]).transpose([0, 2, 1, 3]); // (B, nh, T, hs)
            var v = qkv.slice([0, 0, 2 * C], [-1, -1, C]).reshape([B, T, nHead, C / nHead]).transpose([0, 2, 1, 3]); // (B, nh, T, hs)
            // Compute attention scores ("affinities")
            var att = tf.matMul(q, k.transpose([0, 1, 3, 2])).mul(tf.scalar(1 / Math.sqrt(headSize))); // (B, nh, T, hs) @ (B, nh, hs, T) -> (B, nh, T, T)
            att = tf.where(bias.slice([0, 0, 0, 0], [1, 1, T, T]).equal(0), tf.scalar(-Infinity), att); // (B, nh, T, T)
            att = tf.softmax(att); // (B, nh, T, T)
            att = attnDrop.apply(att);
            var y = tf.matMul(att, v); // (B, nh, T, T) @ (B, nh, T, hs) -> (B, nh, T, hs)
            y = y.transpose([0, 2, 1, 3]).reshape([B, T, C]); // Re-assemble all head outputs side by side (reshape to [B, T, C])
            // Output projection
            y = cProj.apply(y);
            y = residDrop.apply(y);
            return y;
        }); },
    };
    return withLayerHelpers(multiHeadAttention, [cAttn, cProj, attnDrop, residDrop, bias]);
}
// A simple linear layer followed by a non-linearity
function FeedForward(args) {
    var nEmbd = args.nEmbd, residDropout = args.residDropout, nLayer = args.nLayer, name = args.name;
    var cFc = tf.layers.dense({ name: "".concat(name, "-cFc"), inputShape: [nEmbd], units: 4 * nEmbd, activation: 'gelu_new', kernelInitializer: kernelInitializer, biasInitializer: biasInitializer });
    var cProj = tf.layers.dense({ name: "".concat(name, "-cProj"), inputShape: [4 * nEmbd], units: nEmbd, kernelInitializer: projectionKernelInitializer(nLayer), biasInitializer: biasInitializer });
    var drop = tf.layers.dropout({ name: "".concat(name, "-drop"), rate: residDropout });
    var ffwd = {
        apply: function (x) { return tf.tidy(function () {
            var x1 = cFc.apply(x);
            var x2 = cProj.apply(x1);
            return drop.apply(x2);
        }); },
    };
    return withLayerHelpers(ffwd, [cFc, cProj, drop]);
}
// Init all weights, and apply a special scaled init to the residual projections, per GPT-2 paper
// For embedding layer
var embeddingsInitializer = tf.initializers.randomNormal({ mean: 0.0, stddev: 0.02 });
// For linear layer
var projectionKernelInitializer = function (nLayer) { return tf.initializers.randomNormal({ mean: 0.0, stddev: 0.02 / Math.sqrt(2 * nLayer) }); };
var kernelInitializer = tf.initializers.randomNormal({ mean: 0.0, stddev: 0.02 });
var biasInitializer = 'zeros';
// For normalization layers `gammaInitializer` is 'ones' and `betaInitializer` is 'zeros' by default, no need to do anything.
